import { useState, useEffect, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sun,
  CheckSquare,
  Folder,
  Inbox,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  X,
  Layers,
  CheckCircle2,
  Circle,
  MailOpen,
  Sparkles,
  Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useDemoMode } from "@/hooks/use-demo-mode";
import { cn } from "@/lib/utils";

// ─── Step mini-mockups ───────────────────────────────────────────────────────

function TodayPreview() {
  const { t } = useTranslation();
  const tasks = [
    { label: t("tutorial_preview.today_task1"), time: "10 min", done: true },
    { label: t("tutorial_preview.today_task2"), time: "45 min", done: false },
    { label: t("tutorial_preview.today_task3"), time: "20 min", done: false },
  ];
  return (
    <div className="flex flex-col gap-1.5 p-3">
      {tasks.map((task, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: -14 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.13, duration: 0.28 }}
          className={cn(
            "flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[11px]",
            task.done
              ? "bg-primary/10 border border-primary/20"
              : "bg-muted/60 border border-border/50",
          )}
        >
          {task.done ? (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.05, type: "spring", stiffness: 400 }}
            >
              <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0" />
            </motion.div>
          ) : (
            <Circle className="w-3.5 h-3.5 text-muted-foreground/50 shrink-0" />
          )}
          <span className={cn("flex-1 truncate font-medium", task.done && "line-through text-muted-foreground")}>
            {task.label}
          </span>
          <span className="text-muted-foreground shrink-0">{task.time}</span>
        </motion.div>
      ))}
    </div>
  );
}

function ActionsPreview() {
  const { t } = useTranslation();
  const items = [
    { sender: t("tutorial_preview.action_sender1"), subject: t("tutorial_preview.action_subject1"), badge: t("tutorial_preview.action_badge1"), color: "text-blue-600 bg-blue-50" },
    { sender: t("tutorial_preview.action_sender2"), subject: t("tutorial_preview.action_subject2"), badge: t("tutorial_preview.action_badge2"), color: "text-amber-600 bg-amber-50" },
    { sender: t("tutorial_preview.action_sender3"), subject: t("tutorial_preview.action_subject3"), badge: t("tutorial_preview.action_badge3"), color: "text-violet-600 bg-violet-50" },
  ];
  return (
    <div className="flex flex-col gap-1.5 p-3">
      {items.map((item, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.14, duration: 0.28 }}
          className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 bg-muted/60 border border-border/50"
        >
          <MailOpen className="w-3 h-3 text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold truncate">{item.sender}</p>
            <p className="text-[10px] text-muted-foreground truncate">{item.subject}</p>
          </div>
          <motion.span
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: i * 0.14 + 0.2, type: "spring", stiffness: 350 }}
            className={cn("text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0", item.color)}
          >
            {item.badge}
          </motion.span>
        </motion.div>
      ))}
    </div>
  );
}

function ProjectsPreview() {
  const { t } = useTranslation();
  const projects = [
    { name: t("tutorial_preview.project1"), progress: 72, color: "bg-blue-500" },
    { name: t("tutorial_preview.project2"), progress: 45, color: "bg-violet-500" },
    { name: t("tutorial_preview.project3"), progress: 88, color: "bg-primary" },
  ];
  return (
    <div className="flex flex-col gap-2.5 p-3 justify-center h-full">
      {projects.map((p, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: i * 0.12 }}
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] font-medium">{p.name}</span>
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: i * 0.12 + 0.35 }}
              className="text-[10px] text-muted-foreground"
            >
              {p.progress}%
            </motion.span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <motion.div
              className={cn("h-full rounded-full", p.color)}
              initial={{ width: 0 }}
              animate={{ width: `${p.progress}%` }}
              transition={{ delay: i * 0.12 + 0.15, duration: 0.55, ease: "easeOut" }}
            />
          </div>
        </motion.div>
      ))}
    </div>
  );
}

function InboxPreview() {
  const { t } = useTranslation();
  const items = [
    { icon: "🎙️", text: t("tutorial_preview.inbox_item1"), badge: t("tutorial_preview.inbox_badge1") },
    { icon: "💡", text: t("tutorial_preview.inbox_item2"), badge: t("tutorial_preview.inbox_badge2") },
    { icon: "📧", text: t("tutorial_preview.inbox_item3"), badge: t("tutorial_preview.inbox_badge3") },
  ];
  return (
    <div className="flex flex-col gap-1.5 p-3">
      {items.map((item, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: i * 0.16, duration: 0.28 }}
          className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 bg-muted/60 border border-border/50"
        >
          <span className="text-sm shrink-0">{item.icon}</span>
          <span className="text-[10px] flex-1 truncate font-medium">{item.text}</span>
          <motion.span
            initial={{ opacity: 0, x: 6 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.16 + 0.25 }}
            className="text-[9px] font-semibold text-primary bg-primary/10 px-1.5 py-0.5 rounded-full shrink-0"
          >
            {item.badge}
          </motion.span>
        </motion.div>
      ))}
    </div>
  );
}

function ChatPreview() {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<"idle" | "typing" | "reply">("idle");

  useEffect(() => {
    const t1 = setTimeout(() => setPhase("typing"), 350);
    const t2 = setTimeout(() => setPhase("reply"), 1300);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <div className="flex flex-col gap-2 p-3 justify-center">
      <motion.div
        initial={{ opacity: 0, x: 16 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.28 }}
        className="flex justify-end"
      >
        <div className="bg-primary text-primary-foreground text-[10px] px-3 py-1.5 rounded-2xl rounded-tr-sm max-w-[75%] flex items-center gap-1.5">
          <Send className="w-2.5 h-2.5 shrink-0" />
          {t("tutorial_preview.chat_user")}
        </div>
      </motion.div>

      <AnimatePresence>
        {phase === "typing" && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-1 px-2"
          >
            {[0, 0.12, 0.24].map((d, i) => (
              <motion.div
                key={i}
                className="w-1.5 h-1.5 bg-muted-foreground/40 rounded-full"
                animate={{ y: [0, -4, 0] }}
                transition={{ repeat: Infinity, duration: 0.55, delay: d }}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {phase === "reply" && (
          <motion.div
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.28 }}
            className="flex items-start gap-1.5"
          >
            <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <Sparkles className="w-3 h-3 text-primary" />
            </div>
            <div className="bg-muted/80 text-[10px] px-3 py-1.5 rounded-2xl rounded-tl-sm max-w-[85%] leading-relaxed">
              {t("tutorial_preview.chat_ai")}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Step definitions ────────────────────────────────────────────────────────

interface TutorialStep {
  icon: typeof Sun;
  iconColor: string;
  bgColor: string;
  accentClass: string;
  title: string;
  description: string;
  href: string;
  linkLabel: string;
  Preview: () => ReactElement;
}

const STEPS: TutorialStep[] = [
  {
    icon: Sun,
    iconColor: "text-amber-500",
    bgColor: "bg-amber-50",
    accentClass: "bg-amber-400",
    title: "Aujourd'hui",
    description: "Chaque matin, OneNext sélectionne vos 3 à 5 tâches les plus importantes. Finis les to-do lists sans fin — vous savez exactement par où commencer.",
    href: "/app/today",
    linkLabel: "Voir Aujourd'hui →",
    Preview: TodayPreview,
  },
  {
    icon: CheckSquare,
    iconColor: "text-blue-500",
    bgColor: "bg-blue-50",
    accentClass: "bg-blue-400",
    title: "À faire",
    description: "Chaque mail analysé par l'IA devient une action concrète. Répondre, planifier, déléguer — traitez votre boîte en quelques clics.",
    href: "/app/actions",
    linkLabel: "Voir les actions →",
    Preview: ActionsPreview,
  },
  {
    icon: Folder,
    iconColor: "text-violet-500",
    bgColor: "bg-violet-50",
    accentClass: "bg-violet-400",
    title: "Projets",
    description: "Regroupez vos tâches en projets. Chaque projet a ses tâches, notes et événements. Visualisez l'avancement d'un coup d'œil.",
    href: "/app/projects",
    linkLabel: "Voir les projets →",
    Preview: ProjectsPreview,
  },
  {
    icon: Inbox,
    iconColor: "text-green-500",
    bgColor: "bg-green-50",
    accentClass: "bg-green-400",
    title: "Inbox & Captures",
    description: "Envoyez un email à OneNext ou dictez une note vocale. Tout arrive ici et l'IA trie, résume et propose des actions concrètes.",
    href: "/app/inbox",
    linkLabel: "Voir l'inbox →",
    Preview: InboxPreview,
  },
  {
    icon: MessageSquare,
    iconColor: "text-rose-500",
    bgColor: "bg-rose-50",
    accentClass: "bg-rose-400",
    title: "Chat IA",
    description: "Votre assistant qui connaît tout votre contexte. Demandez-lui de créer des tâches, résumer votre semaine ou planifier votre sprint.",
    href: "/app/chat",
    linkLabel: "Ouvrir le chat →",
    Preview: ChatPreview,
  },
];

const STORAGE_KEY = "onenext.demoTutorial.step";

function readStoredStep(): number {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === null) return 0;
    const n = parseInt(v, 10);
    return isNaN(n) ? -1 : n;
  } catch {
    return 0;
  }
}

function saveStep(step: number) {
  try {
    if (step < 0) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, String(step));
    }
  } catch {}
}

// ─── Component ───────────────────────────────────────────────────────────────

interface DemoTutorialProps {
  onClose?: () => void;
}

export default function DemoTutorial({ onClose }: DemoTutorialProps) {
  const { active } = useDemoMode();
  const [step, setStep] = useState<number>(() => readStoredStep());
  const [direction, setDirection] = useState(1);
  const [, setLocation] = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let cleanup: (() => void) | undefined;
    if (active && step >= 0) {
      const t = setTimeout(() => setVisible(true), 300);
      cleanup = () => clearTimeout(t);
    } else {
      setVisible(false);
    }
    return cleanup;
  }, [active, step]);

  if (!active || step < 0) return null;

  const current = STEPS[step];
  const Icon = current.icon;
  const Preview = current.Preview;
  const isLast = step === STEPS.length - 1;

  const handleNext = () => {
    if (isLast) {
      handleClose();
    } else {
      setDirection(1);
      const next = step + 1;
      setStep(next);
      saveStep(next);
      setLocation(STEPS[next].href);
    }
  };

  const handlePrev = () => {
    if (step === 0) return;
    setDirection(-1);
    const prev = step - 1;
    setStep(prev);
    saveStep(prev);
    setLocation(STEPS[prev].href);
  };

  const handleClose = () => {
    setStep(-1);
    saveStep(-1);
    setVisible(false);
    onClose?.();
  };

  const slideVariants = {
    enter: (d: number) => ({ opacity: 0, x: d * 32 }),
    center: { opacity: 1, x: 0 },
    exit: (d: number) => ({ opacity: 0, x: -d * 32 }),
  };

  return (
    <div
      className={cn(
        "fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] max-w-sm transition-all duration-300",
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none",
      )}
    >
      <div className="rounded-2xl border border-border bg-background/96 backdrop-blur-md shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <div className="flex items-center gap-2">
            <Layers className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
              Visite guidée — {step + 1}/{STEPS.length}
            </span>
          </div>
          <button
            onClick={handleClose}
            className="rounded-full p-1 hover:bg-muted text-muted-foreground transition-colors"
            aria-label="Fermer le tutoriel"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Animated preview pane */}
        <div className="relative h-[108px] border-y border-border bg-muted/20 overflow-hidden">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={step}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.28, ease: "easeInOut" }}
              className="absolute inset-0"
            >
              <Preview />
            </motion.div>
          </AnimatePresence>

          {/* Step colour accent stripe */}
          <div className={cn("absolute left-0 top-0 bottom-0 w-0.5", current.accentClass)} />
        </div>

        {/* Step content */}
        <div className="px-4 pt-3 pb-4">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={step}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.22, ease: "easeInOut" }}
            >
              <div className="flex items-start gap-2.5 mb-2">
                <div className={cn("mt-0.5 w-7 h-7 rounded-lg flex items-center justify-center shrink-0", current.bgColor)}>
                  <Icon className={cn("w-4 h-4", current.iconColor)} />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-sm leading-snug">{current.title}</h3>
                  <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                    {current.description}
                  </p>
                </div>
              </div>

              <button
                onClick={() => setLocation(current.href)}
                className="text-[11px] font-semibold text-primary hover:underline mb-3 block"
              >
                {current.linkLabel}
              </button>
            </motion.div>
          </AnimatePresence>

          {/* Footer: dots + navigation */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              {STEPS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setDirection(i > step ? 1 : -1);
                    setStep(i);
                    saveStep(i);
                    setLocation(STEPS[i].href);
                  }}
                  className={cn(
                    "rounded-full transition-all duration-200",
                    i === step
                      ? "w-5 h-2 bg-primary"
                      : "w-2 h-2 bg-muted-foreground/25 hover:bg-muted-foreground/50",
                  )}
                  aria-label={`Étape ${i + 1}`}
                />
              ))}
              <span className="ml-1.5 text-[11px] font-semibold tabular-nums text-muted-foreground">
                {step + 1}<span className="font-normal opacity-50">/{STEPS.length}</span>
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <Button size="sm" variant="ghost" onClick={handlePrev} disabled={step === 0} className="h-7 w-7 p-0">
                <ChevronLeft className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" onClick={handleNext} className="h-7 px-3 text-xs gap-1">
                {isLast ? "Terminer" : "Suivant"}
                {!isLast && <ChevronRight className="w-3.5 h-3.5" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

